/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Mon Apr 08 2024
 **/

import { Fragment } from "react";

import { useFileManager } from "@/frameworks/core/hooks";
import { usePersistUser } from "@/frameworks/core/persistence";
import { Typography } from "@/frameworks/designStuff/components";
import { Maybe } from "@/frameworks/libraries/components";
import { _T } from "@rfice/language";

export default function FileUploader({
	file,
	isShare3D
}: {
	file: Required<FileBox.Folders.GetFolderFileListResponse>["content"][number];
	isShare3D?: boolean;
}) {
	const user = usePersistUser(file?.createBy ? file?.createBy : "");
	const { currentFolder } = useFileManager();
	const isMyFile = Boolean(user?.me);
	return (
		<Fragment>
			<ChannelName
				file={file}
				isShare3D={isShare3D}
				folderType={currentFolder.folderType}
				isAll={Boolean(currentFolder.all)}
				userName={user?.name}
			/>
			<Maybe
				test={currentFolder.folderType !== "CHAT" && isMyFile}
				fallback={
					<Typography typoSize="Body5" typoColor="neutral_50_100">
						•
					</Typography>
				}
			/>
		</Fragment>
	);
}

function ChannelName({
	file,
	isShare3D,
	folderType,
	isAll,
	userName
}: {
	file: Required<FileBox.Folders.GetFolderFileListResponse>["content"][number];
	isShare3D?: boolean;
	isAll: boolean;
	folderType: "CHAT" | "SPACE" | "INDIVIDUAL" | "ALL";
	userName?: string;
}) {
	const getStyle = (isDisplaySpace?: boolean) => {
		if (isDisplaySpace) return { maxWidth: "55px" };
	};

	const getReferenceName = (folderType?: "CHAT" | "SPACE" | "INDIVIDUAL" | "ALL", isAll?: boolean) => {
		if (file.chatRoom?.space && folderType === "SPACE" && !isAll) {
			if (file.chatRoom.space?.rooms?.length > 0) {
				const roomName = file.chatRoom.space?.rooms[0].roomName;
				return roomName ?? "";
			}
			return file.chatRoom.space?.spaceName ?? "";
		}
		return file.referenceName || file.space?.spaceName || "";
	};
	const renderFileName = (name: string) => {
		return (
			<Typography
				maxLine={1}
				ellipsis
				typoSize="Body5"
				typoColor="neutral_80_100"
				style={getStyle(file.referenceType === "SPACE" || !!file?.space)}
			>
				{name}
			</Typography>
		);
	};
	const renderUserOrSeparator = (hasReferenceName: boolean) => (
		<Fragment>
			<Maybe test={hasReferenceName}>
				<Typography typoSize="Body5" typoColor="neutral_50_100">
					•
				</Typography>
			</Maybe>
			{renderFileName(userName ?? _T("삭제된 사용자"))}
		</Fragment>
	);

	if (file.referenceType === "INDIVIDUAL" && isShare3D) {
		return (
			<Typography maxLine={1} ellipsis typoSize="Body5" typoColor={"neutral_00_100"}>
				{_T("마이룸") + " "}
				<Typography typoSize="Body5" typoColor="neutral_50_100">
					•
				</Typography>
			</Typography>
		);
	}

	if (folderType === "CHAT") {
		return renderFileName(userName ?? _T("삭제된 사용자"));
	}

	if (folderType === "SPACE" || (folderType === "ALL" && isAll) || file.referenceType === "SPACE") {
		return (
			<Fragment>
				<Typography
					maxLine={1}
					ellipsis
					typoSize="Body5"
					typoColor="neutral_80_100"
					style={getStyle(file.referenceType === "SPACE" || !!file?.space)}
				>
					{getReferenceName(folderType, isAll)}
				</Typography>
				{renderUserOrSeparator(Boolean(getReferenceName()?.length))}
			</Fragment>
		);
	}
	return null;
}
